import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  getPromoItem,
  createPromo,
  updatePromo,
  setPromoLocale,
} from "store/cms/promo/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ILocaleData, ModalPromoTabs } from "configs/types";

import Button from "components/buttons/Button";
import PromoMainTab from "./tabs/PromoMainTab";
import LocalTab from "../globalTabs/LocalTab";
import { apiRoutes } from "../../../configs/apiRoutes";
import { Modal } from "components/Modal/Modal";
import styles from "./CmsPromoEditModal.module.scss";
import PromoTrailTab from "./tabs/PromoTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";

const CmsPromoEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector((store) => store.promo.isItemFetched);
  const isItemFetching = useAppSelector((store) => store.promo.isItemFetching);
  const payload = useAppSelector((store) => store.promo.payload);

  const [imgPreviewBig, setImgPreviewBig] = useState<string>("");
  const [imgPreviewSmall, setImgPreviewSmall] = useState<string>("");
  const [localeData, setLocaleData] = useState<ILocaleData>();
  const [initialLocaleData, setInitialLocaleData] = useState<ILocaleData>();
  const [localeReady, setLocaleReady] = useState(false);

  const [tabs] = useState<ModalPromoTabs[]>([
    ModalPromoTabs.main,
    ModalPromoTabs.local,
    ModalPromoTabs.trail,
    // ModalPromoTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModalPromoTabs>(
    ModalPromoTabs.main,
  );

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      dispatch(getPromoItem(searchParams.get("entityId") ?? ""));
    }
  }, [searchParams, isItemFetched]);

  useEffect(() => {
    if (payload?.imageBig) {
      if (typeof payload.imageBig === "string") {
        setImgPreviewBig(payload.imageBig);
      } else {
        const file = payload?.imageBig;
        if (file && file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setImgPreviewBig(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
      }
    }
    if (payload?.imageSmall) {
      if (typeof payload.imageSmall === "string") {
        setImgPreviewSmall(payload.imageSmall);
      } else {
        const file = payload?.imageSmall;
        if (file && file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setImgPreviewSmall(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  }, [payload]);

  useEffect(() => {
    formErrors.resetErrors();
  }, [activeTab]);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }
    return true;
  };

  const handleTabClick = (tab: ModalPromoTabs) => {
    if (!isEdit && tab !== ModalPromoTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
    if (tab !== activeTab) {
      setLocaleReady(false);
    }
  };

  const renderTab = () => {
    if (activeTab === ModalPromoTabs.main) {
      return (
        <PromoMainTab
          payload={payload}
          handleData={handleData}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }
    if (activeTab === ModalPromoTabs.local) {
      return (
        <LocalTab
          id={searchParams.get("entityId") ?? undefined}
          currentData={localeData}
          handleData={(data) => setLocaleData({ ...localeData, ...data })}
          onInitialData={setInitialLocaleData}
          url={apiRoutes.getPromoItemLocale}
          type="promo"
          isAiAssist
          title={payload?.title}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
          readOnly={!permissions?.update}
          onReadyChange={setLocaleReady}
          onLoadError={(error) => handleApiError({ error, t, dispatch })}
        />
      );
    }
    if (activeTab === ModalPromoTabs.trail || permissions?.update) {
      return <PromoTrailTab entityId={searchParams.get("entityId")!} />;
    }
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  const handleSetLocale = async () => {
    if (!localeData) return;

    const { geo, locale, isActive } = localeData;
    const textChanges = getChangedFields(
      initialLocaleData,
      localeData,
      ["title", "content", "promoLabel"],
      {
        title: normalizeOptionalText,
        content: normalizeSeoHtml,
        promoLabel: normalizeOptionalText,
      },
    );

    try {
      await dispatch(
        setPromoLocale({
          id: searchParams.get("entityId") ?? "",
          data: {
            geo,
            locale,
            ...textChanges,
            isActive,
          },
        }),
      ).unwrap();
      setInitialLocaleData(localeData);

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const handleApply = async () => {
    if (activeTab === ModalPromoTabs.main) {
      formErrors.resetErrors();

      try {
        if (isEdit) {
          await dispatch(updatePromo(payload)).unwrap();
        } else {
          const newId = await dispatch(createPromo(payload)).unwrap();

          navigate(`${location.pathname}?entityId=${newId}`);
        }

        dispatch(
          showNotification({
            title: t("success"),
            message: t(isEdit ? "entityUpdateSuccess" : "entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        if (!isEdit) {
          handleCloseClick();
        }
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    if (activeTab === ModalPromoTabs.local) {
      await handleSetLocale();
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("Promo")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content className={`${styles.cmsPromoContentWrapper} ${
            activeTab === ModalPromoTabs.trail ? styles.trailContentWrapper : ""
          }`}>
            <div className="promo-content">{renderTab()}</div>
          </Modal.Content>

          {activeTab !== ModalPromoTabs.trail && permissions?.update && (
            <Modal.Footer>
              {activeTab === ModalPromoTabs.local && !localeReady ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  <Button
                    isPrimary
                    isLoad={isItemFetching}
                    isSecondary
                    handleClick={handleApply}
                    txt={t("save")}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsPromoEditModal;
